import { useCallback, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import TextareaAutosize from 'react-textarea-autosize'
import { useQuery } from '@apollo/client'
import {
  faPenToSquare,
  faSpinner,
  faTurnDownRight,
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { isEmpty } from 'lodash-es'
import { observer } from 'mobx-react'

import {
  Checkbox,
  Heading,
  LinkButton,
  PrimaryButton,
  TertiaryButton,
  Textarea,
} from 'Components/_theme'
import { FormattedCurrency } from 'Components/FormattedCurrency/FormattedCurrency'
import { Icon } from 'Components/Icon/Icon'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'

import { useStore } from 'Stores/index'

import { useActiveUser, useIsOrganisation } from 'Hooks'

import { isActiveLanguage } from 'Utilities'

import { E2E_NEW_ORDER_SUBMIT } from 'Constants/e2e'
import {
  getRentCategoryTerms,
  getRentCategoryTermsVariables,
} from 'Constants/graphql/queries/detail/__generated__/getRentCategoryTerms'
import { GET_RENT_CATEGORY_TERMS } from 'Constants/graphql/queries/detail/GetRentCategoryTerms'
import { SupportedLanguagesEnum } from 'Constants/locale'

import { ImagePlaceholderTypesEnum } from 'Types/imagePlaceholders.type'

import { OrderDeviceResponseVariant } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'

import { OrderStickyFooter } from '../OrderStickyFooter/OrderStickyFooter'
import {
  OrderSummaryDeviceRowRendererProps,
  OrderSummaryProps,
  OrderSummaryTerms,
} from './OrderSummary.types'
import { OrderSummaryCustomerLabelInput } from './OrderSummaryCustomerLabelInput'
import { OrderSummaryQuantityInput } from './OrderSummaryQuantityInput'

export const OrderSummary = observer((props: OrderSummaryProps) => {
  const {
    chosenDevice,
    chosenAccessories,
    chosenShippingAddress,
    isLoading,
    onOrderButtonClick,
    totalPrice,
    onBackButtonClick,
    onChangeDeviceButtonClick,
    onSetChosenDeviceQuantity,
    onSetAccessoryLabel,
    onSetAccessoryQuantity,
    onSetChosenDeviceLabel,
    remarks,
    setRemarks,
    isEarlyUpgrade,
    earlyUpgradeFee,
    isNewOrder,
    orderDeviceResponseVariant,
  } = props

  const { portalConfigStore, portalSettingsStore } = useStore()
  const { portalConfig } = portalConfigStore
  const {
    i18n: { language },
  } = portalSettingsStore
  const isOrganisation = useIsOrganisation()

  const isBundle = chosenDevice?.isBundle

  const chosenDeviceName = isBundle
    ? chosenDevice.bundleName
    : chosenDevice?.deviceDefinition.name

  const chosenDeviceImage = isBundle
    ? chosenDevice.bundleImage
    : chosenDevice?.deviceDefinition.imageURL

  const enableCustomerLabels = portalConfig?.enableCustomerLabels ?? false

  const intl = useIntl()
  const isGermanLanguageActive = isActiveLanguage(SupportedLanguagesEnum.DE)

  const { activeOrganisation, activeEmployee } = useActiveUser()

  const { data: rentCategory } = useQuery<
    getRentCategoryTerms,
    getRentCategoryTermsVariables
  >(GET_RENT_CATEGORY_TERMS, {
    variables: {
      id: chosenDevice?.rentCategoryId ?? '',
    },
  })

  // ORDER TERMS LOGIC BLOCK
  const [termsAcceptance, setTermsAcceptance] = useState<OrderSummaryTerms>({
    defaultTerms: false,
    orgTerms: false,
    productOfferTerms: false,
    rentCategoryTerms: false,
  })

  const germanTerms =
    activeOrganisation?.individualTermsDE ||
    activeEmployee?.organisation?.individualTermsDE

  const englishTerms =
    activeOrganisation?.individualTermsEN ||
    activeEmployee?.organisation?.individualTermsEN

  const hasOrganisationTerms = isGermanLanguageActive
    ? !!germanTerms
    : !!englishTerms

  const showOrganisationTerms =
    orderDeviceResponseVariant === OrderDeviceResponseVariant.CATALOG
      ? chosenDevice?.hasDeviceWithCoPaymentPrice && hasOrganisationTerms
      : !!germanTerms || !!englishTerms

  const showRentCategoryTerms =
    orderDeviceResponseVariant === OrderDeviceResponseVariant.RENT_CATEGORY &&
    !!chosenDevice?.copaymentNet &&
    !!rentCategory?.rentCategory.individualTerms

  const productOfferTermsLabel =
    chosenDevice?.termsLabels?.find(offerTerm => offerTerm.locale === language)
      ?.content || ''

  const hasProductOfferTerms =
    !!chosenDevice?.termsLabels &&
    !isEmpty(chosenDevice?.termsLabels) &&
    productOfferTermsLabel

  const showProductOfferTerms =
    orderDeviceResponseVariant === OrderDeviceResponseVariant.CATALOG &&
    chosenDevice?.hasDeviceWithCoPaymentPrice &&
    hasProductOfferTerms

  const showDefaultTerms =
    orderDeviceResponseVariant === OrderDeviceResponseVariant.CATALOG
      ? chosenDevice?.hasDeviceWithCoPaymentPrice &&
        !showProductOfferTerms &&
        !showOrganisationTerms
      : false

  const showNestedView =
    chosenDevice?.bundleImage !== chosenDevice?.deviceDefinition.imageURL ||
    chosenDevice?.bundleName !== chosenDevice?.deviceDefinition.name

  const generateOrganisationTerms = useMemo(() => {
    if (germanTerms && isGermanLanguageActive) {
      return germanTerms
    }

    if (englishTerms && !isGermanLanguageActive) {
      return englishTerms
    }

    return intl.formatMessage({
      id: 'NewOrderSummary_accept_terms',
    })
  }, [germanTerms, isGermanLanguageActive, englishTerms, intl])

  const rentCategoryTermsLabel =
    rentCategory?.rentCategory.individualTerms || ''

  const terms = useMemo(
    () => [
      {
        checked: termsAcceptance.defaultTerms,
        label: intl.formatMessage({
          id: 'NewOrderSummary_accept_terms',
        }),
        name: 'defaultTerms',
        show: showDefaultTerms,
      },
      {
        checked: termsAcceptance.orgTerms,
        label: generateOrganisationTerms,
        name: 'orgTerms',
        show: showOrganisationTerms,
      },
      {
        checked: termsAcceptance.rentCategoryTerms,
        label: rentCategoryTermsLabel,
        name: 'rentCategoryTerms',
        show: showRentCategoryTerms,
      },
      {
        checked: termsAcceptance.productOfferTerms,
        label: productOfferTermsLabel,
        name: 'productOfferTerms',
        show: showProductOfferTerms,
      },
    ],
    [
      generateOrganisationTerms,
      intl,
      productOfferTermsLabel,
      rentCategoryTermsLabel,
      showDefaultTerms,
      showOrganisationTerms,
      showProductOfferTerms,
      showRentCategoryTerms,
      termsAcceptance.defaultTerms,
      termsAcceptance.orgTerms,
      termsAcceptance.productOfferTerms,
      termsAcceptance.rentCategoryTerms,
    ],
  )
  // END OF ORDER TERMS LOGIC BLOCK

  const isRentCategoryDevice =
    orderDeviceResponseVariant === OrderDeviceResponseVariant.RENT_CATEGORY

  const renderSummaryHeadingRow = useMemo(
    () => (
      <Row className="order-container grey-container p-2 px-4 d-flex justify-content-between align-items-center">
        <Col className="ps-0" xs={isNewOrder ? 6 : 8}>
          <Heading as={6} size={3}>
            <FormattedMessage id="NewOrderSummary_device" />
          </Heading>
        </Col>

        <Col className="text-end" xs={2}>
          <Heading as={6} size={3}>
            <FormattedMessage id="NewOrderSummary_rental_duration" />
          </Heading>
        </Col>

        {isNewOrder && isOrganisation && (
          <Col className="text-end" xs={2}>
            <Heading as={6} size={3}>
              <FormattedMessage id="NewOrderSummary_quantity" />
            </Heading>
          </Col>
        )}

        <Col className="text-end pe-0" xs={2}>
          <Heading as={6} size={3}>
            <FormattedMessage id="NewOrderSummary_price" />
          </Heading>
        </Col>
      </Row>
    ),
    [isNewOrder, isOrganisation],
  )

  const renderSummaryDeviceRow = useCallback(
    (props: OrderSummaryDeviceRowRendererProps) => {
      const {
        isAddon,
        isBundleRow,
        isBundleItemRow,
        isCreateSeparateRental,
        deviceName,
        deviceImage,
        deviceQuantity,
        deviceRentalDuration,
        deviceId,
        devicePrice,
        deviceCustomerLabel,
      } = props

      const showCustomerLabel =
        isOrganisation && enableCustomerLabels && !isBundleRow

      const hideDurationAndPrice = isAddon && !isCreateSeparateRental

      return (
        <Row
          key={deviceId}
          className={clsx(
            'px-4 py-6 device-overview',
            isNewOrder && !isOrganisation && 'justify-content-between',
          )}
        >
          <Col
            className={clsx(isBundleItemRow && 'd-flex')}
            xs={isNewOrder ? 6 : 8}
          >
            {isBundleItemRow && (
              <Icon className="me-2" icon={faTurnDownRight} size={20} />
            )}
            <div className="d-flex align-items-center">
              <ImageWithFallback
                alt={deviceName || ''}
                placeholderType={ImagePlaceholderTypesEnum.newOrderFlow}
                src={deviceImage || ''}
              />

              <strong className="ms-2 ms-md-6">{deviceName}</strong>
            </div>
          </Col>

          <Col className="d-flex justify-content-end align-items-center" xs={2}>
            {!isBundleRow && !hideDurationAndPrice && (
              <>
                {deviceRentalDuration}&nbsp;
                <FormattedMessage id="NewOrderSummary_months" />
              </>
            )}
          </Col>

          {isNewOrder && isOrganisation && (
            <Col
              className="d-flex justify-content-end align-items-center"
              xs={2}
            >
              {isBundleItemRow || hideDurationAndPrice ? (
                deviceQuantity
              ) : (
                <OrderSummaryQuantityInput
                  controlId="deviceQuantity"
                  defaultValue={deviceQuantity}
                  onChangeInput={value => {
                    onSetChosenDeviceQuantity(value)
                  }}
                />
              )}
            </Col>
          )}

          {!hideDurationAndPrice && (
            <Col
              className="d-flex justify-content-end align-items-center"
              xs={2}
            >
              {!isBundleRow && <FormattedCurrency value={devicePrice} />}
            </Col>
          )}

          {showCustomerLabel && (
            <Col xs={6}>
              <OrderSummaryCustomerLabelInput
                controlId={`customDeviceLabel-${deviceId}`}
                defaultValue={deviceCustomerLabel || ''}
                onChangeLabel={event =>
                  onSetChosenDeviceLabel(event.currentTarget.value)
                }
              />
            </Col>
          )}
        </Row>
      )
    },
    [
      enableCustomerLabels,
      isNewOrder,
      isOrganisation,
      onSetChosenDeviceLabel,
      onSetChosenDeviceQuantity,
    ],
  )

  return (
    <>
      <Container className="OrderFlow-summary" fluid>
        <Row className="overflow-auto">
          <Col className="scrollable-on-mobile">
            {renderSummaryHeadingRow}

            {(showNestedView || !isBundle) &&
              renderSummaryDeviceRow({
                deviceCustomerLabel: chosenDevice?.customerLabel || '',
                deviceImage: chosenDeviceImage || '',
                deviceName: chosenDeviceName || '',
                devicePrice: chosenDevice?.copaymentNet || 0,
                deviceQuantity: chosenDevice?.quantity || 1,
                deviceRentalDuration:
                  chosenDevice?.minimumContractDuration || 0,
                isBundleRow: isBundle,
              })}

            {isBundle && (
              <>
                {renderSummaryDeviceRow({
                  deviceCustomerLabel: chosenDevice?.customerLabel || '',
                  deviceImage: chosenDevice?.deviceDefinition.imageURL || '',
                  deviceName: chosenDevice?.deviceDefinition.name || '',
                  devicePrice: chosenDevice?.copaymentNet || 0,
                  deviceQuantity: chosenDevice?.quantity || 1,
                  deviceRentalDuration:
                    chosenDevice?.minimumContractDuration || 0,
                  isBundleItemRow: showNestedView,
                })}

                {chosenDevice?.addonDevices?.map(addonDevice =>
                  renderSummaryDeviceRow({
                    deviceId: addonDevice.id,
                    deviceImage: addonDevice.deviceDefinition.imageURL || '',
                    deviceName: addonDevice.deviceDefinition.name,
                    devicePrice: addonDevice.copaymentNet,
                    deviceQuantity: addonDevice.quantity,
                    deviceRentalDuration:
                      addonDevice.minimumContractDuration || 0,
                    isAddon: true,
                    isBundleItemRow: showNestedView,
                    isCreateSeparateRental: addonDevice.isCreateSeparateRental,
                  }),
                )}
              </>
            )}

            {[...chosenAccessories].map(chosenAccessory => {
              const [_id, accessory] = chosenAccessory

              return (
                <Row
                  key={accessory.rentCategoryId + accessory.deviceDefinition.id}
                  className={clsx(
                    'd-flex px-4 py-6 device-overview',
                    isNewOrder && !isOrganisation && 'justify-content-between',
                  )}
                >
                  <Col className="ps-0" xs={isNewOrder ? 6 : 8}>
                    <div className="d-flex align-items-center">
                      <ImageWithFallback
                        alt={accessory.deviceDefinition.name}
                        placeholderType={ImagePlaceholderTypesEnum.newOrderFlow}
                        src={accessory.deviceDefinition.imageURL || ''}
                      />

                      <strong className="ms-2 ms-md-6">
                        {accessory.deviceDefinition.name}
                      </strong>
                    </div>
                  </Col>

                  <Col
                    className="d-flex justify-content-end align-items-center"
                    xs={2}
                  >
                    {(isRentCategoryDevice ||
                      accessory.isCreateSeparateRental) && (
                      <>
                        {accessory?.minimumContractDuration}&nbsp;
                        <FormattedMessage id="NewOrderSummary_months" />
                      </>
                    )}
                  </Col>

                  {isNewOrder && isOrganisation && (
                    <Col
                      className="d-flex justify-content-end align-items-center"
                      xs={2}
                    >
                      {isRentCategoryDevice ||
                      accessory.isCreateSeparateRental ? (
                        <OrderSummaryQuantityInput
                          controlId="accessoryQuantity"
                          defaultValue={accessory.quantity ?? 1}
                          onChangeInput={value => {
                            onSetAccessoryQuantity(value, accessory)
                          }}
                        />
                      ) : (
                        accessory.quantity
                      )}
                    </Col>
                  )}

                  {(isRentCategoryDevice ||
                    accessory.isCreateSeparateRental) && (
                    <Col
                      className="d-flex justify-content-end align-items-center"
                      xs={2}
                    >
                      <FormattedCurrency value={accessory.copaymentNet || 0} />
                    </Col>
                  )}

                  {isOrganisation && enableCustomerLabels && (
                    <Col className="ps-0" xs={6}>
                      <OrderSummaryCustomerLabelInput
                        controlId="customAccessoryLabel"
                        defaultValue={accessory.customerLabel ?? ''}
                        onChangeLabel={event =>
                          onSetAccessoryLabel(
                            event.currentTarget.value,
                            accessory,
                          )
                        }
                      />
                    </Col>
                  )}
                </Row>
              )
            })}
          </Col>
        </Row>

        <Row
          className={clsx({
            'pb-4': isEarlyUpgrade,
            'pb-6': !isEarlyUpgrade,
          })}
        >
          <Col>
            <Row className="price-container px-4 d-flex justify-content-between align-items-center">
              <Col className="ps-0" md="auto" xs={8}>
                <Heading as={5} size={4}>
                  <FormattedMessage id="NewOrderSummary_total" />
                </Heading>
              </Col>

              <Col className="text-end pe-0">
                <Heading as={5} className="mt-1" size={4}>
                  <FormattedCurrency value={totalPrice.totalWithoutTax} />
                </Heading>
              </Col>
            </Row>

            {portalConfig?.showCopaymentWithAndWithoutTax && (
              <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                <Col className="ps-0" md="auto" xs={8}>
                  <Heading as={5} size={4}>
                    <FormattedMessage id="NewOrderSummary_total_gross" />
                  </Heading>
                </Col>

                <Col className="text-end pe-0">
                  <Heading as={5} className="mt-1" size={4}>
                    <FormattedCurrency value={totalPrice.totalWithTax} />
                  </Heading>
                </Col>
              </Row>
            )}

            {isOrganisation &&
              portalConfig?.showRentalBasisAmountAtOrderFlow &&
              !!totalPrice.totalBasis &&
              !chosenDevice?.forcePriceHide && (
                <Row className="price-container px-4 d-flex justify-content-between align-items-center">
                  <Col className="ps-0" md="auto" xs={8}>
                    <Heading as={5} size={4}>
                      <FormattedMessage id="NewOrderSummary_total_basis" />
                    </Heading>
                  </Col>

                  <Col className="text-end pe-0">
                    <Heading as={5} className="mt-1" size={4}>
                      <FormattedCurrency value={totalPrice.totalBasis} />
                    </Heading>
                  </Col>
                </Row>
              )}

            {!!chosenDevice?.calculatedNet && (
              <Row className="position-relative order-container green-container mt-4 px-4 d-flex justify-content-between align-items-center">
                <Col
                  className="d-flex align-items-center ps-0"
                  md="auto"
                  xs={8}
                >
                  <LinkButton
                    aria-label={intl.formatMessage({
                      id: 'Edit',
                    })}
                    className="text-secondary me-1 p-0"
                    onClick={onChangeDeviceButtonClick}
                  >
                    <Icon icon={faPenToSquare} size={14} />
                  </LinkButton>

                  <Heading as={5} size={4}>
                    &nbsp;
                    <FormattedMessage id="NewOrderSummary_net_total" />
                  </Heading>
                </Col>

                <Col className="d-flex align-items-center justify-content-end pe-0">
                  <div>
                    <Heading as={5} className="mt-1" size={4}>
                      ≈&nbsp;
                      <FormattedCurrency
                        value={totalPrice.totalCalculatedNet}
                      />
                    </Heading>
                  </div>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        {isEarlyUpgrade && (
          <Row className="mt-2 pb-6">
            <Col>
              <Row className="blue-container p-4 d-flex justify-content-between">
                <Col className="ps-0" md="auto" xs={8}>
                  <Heading as={5} className="m-0 mb-2" size={4}>
                    <FormattedMessage id="Upgrade_Fee_ExclVAT" />
                  </Heading>

                  <p className="m-0">
                    <FormattedMessage id="Order_upgrade_fee_info" />
                  </p>
                </Col>

                <Col className="d-flex align-items-center justify-content-end pe-0">
                  <Heading as={5} className="mt-1" size={4}>
                    <FormattedCurrency value={earlyUpgradeFee || 0} />
                  </Heading>
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        <Row className="pt-4">
          <Col className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Heading as={6} size={5}>
                <FormattedMessage id="NewOrderSummary_shipping_address" />
              </Heading>

              <TertiaryButton
                aria-label={intl.formatMessage({ id: 'Edit_address' })}
                asIcon={faPenToSquare}
                onClick={onBackButtonClick}
              />
            </div>

            <Textarea
              aria-label="ShippingAddress"
              className="mb-6"
              cols={3}
              id="ShippingAddress"
              onChange={event => event.preventDefault()}
              value={`${chosenShippingAddress?.name}${
                chosenShippingAddress?.nameAddition
                  ? ` ${chosenShippingAddress.nameAddition}\n`
                  : '\n'
              }${chosenShippingAddress?.street}${
                chosenShippingAddress?.streetAddition
                  ? ` ${chosenShippingAddress.streetAddition}\n`
                  : '\n'
              }${chosenShippingAddress?.zipcode} ${
                chosenShippingAddress?.city
              } ${chosenShippingAddress?.country}`}
            />

            <Heading as={6} className="pt-6 pb-2" size={5}>
              <FormattedMessage id="NewOrderSummary_remarks" />
            </Heading>
            <Textarea
              aria-label="remarks"
              customComponent={TextareaAutosize}
              defaultValue={remarks || ''}
              id="remarks"
              isResizable
              onChange={event => setRemarks(event.currentTarget.value)}
            />

            {terms.map(
              ({ show, name, ...rest }) =>
                show && (
                  <Checkbox
                    key={name}
                    className="text-linebreak mt-6"
                    id={name}
                    name={name}
                    onChange={() =>
                      setTermsAcceptance(termsAcceptance => ({
                        ...termsAcceptance,
                        [name]:
                          !termsAcceptance[name as keyof OrderSummaryTerms],
                      }))
                    }
                    {...rest}
                  />
                ),
            )}
          </Col>
        </Row>
      </Container>

      <OrderStickyFooter
        handlePreviousAction={onBackButtonClick}
        renderNextButtonElement={() => (
          <PrimaryButton
            aria-label={intl.formatMessage({
              id: 'NewOrderSummary_order',
            })}
            className="ms-1"
            data-e2e={E2E_NEW_ORDER_SUBMIT}
            disabled={
              (showDefaultTerms && !termsAcceptance.defaultTerms) ||
              (showOrganisationTerms && !termsAcceptance.orgTerms) ||
              (showRentCategoryTerms && !termsAcceptance.rentCategoryTerms) ||
              (showProductOfferTerms && !termsAcceptance.productOfferTerms) ||
              isLoading
            }
            id="order"
            onClick={onOrderButtonClick}
          >
            {isLoading ? (
              <Icon icon={faSpinner} spin />
            ) : (
              <FormattedMessage id="NewOrderSummary_order" />
            )}
          </PrimaryButton>
        )}
      />
    </>
  )
})
